import { ReactElement } from 'react';
import { useDraggable } from '@dnd-kit/core';
import styles from './MenuSection.module.scss';
import MenuItemCard from '../MenuItemCard';
import SortableMenuItemCard from '../MenuItemCard/SortableMenuItemCard';
import { IMenuItem } from 'src/types/menu.types';

export interface IMenuSectionItemCardProps {
  item: IMenuItem;
  menuId: string;
  sectionId: string;
  displayOrderLeft?: number;
  displayOrderRight?: number;
  isPreviewed: boolean;
  isDraggable: boolean;
  isViewOnly?: boolean;
}

// dnd-kit measures the active draggable from the node registered under its id, and drives
// auto-scroll from that rect. Previewing the card into another section replaces its sortable with
// this placeholder, so without a registration here the active node vanishes mid-gesture and
// auto-scroll falls back to drag-start geometry - which makes the page sawtooth while the pointer
// sits in a scroll zone. Registering the placeholder keeps the rect live; disabled, because the
// placeholder must never start a drag of its own.
const PreviewedItemCard = ({ itemId }: { itemId: string }): ReactElement => {
  const { setNodeRef } = useDraggable({ id: itemId, disabled: true });

  return <div ref={setNodeRef} className={styles['previewed-card']} data-testid={`previewed-card-${itemId}`} />;
};

const MenuSectionItemCard = ({
  item,
  menuId,
  sectionId,
  displayOrderLeft,
  displayOrderRight,
  isPreviewed,
  isDraggable,
  isViewOnly,
}: IMenuSectionItemCardProps): ReactElement => {
  if (isPreviewed) {
    return <PreviewedItemCard itemId={item.itemId} />;
  }

  const cardProps = {
    ...item,
    menuId,
    itemId: item.itemId,
    parentItemId: item.parentItemId,
    sectionId,
    displayOrderLeft,
    displayOrderRight,
    isViewOnly,
  };

  return isDraggable ? <SortableMenuItemCard {...cardProps} /> : <MenuItemCard {...cardProps} />;
};

export default MenuSectionItemCard;
